import { ErrorDomainSource } from "../../../api/sentryIssues";

/**
 * One colour per domain, mid-toned so the chips stay legible in both the light
 * and dark themes. Domains not listed fall back to grey, which is also what an
 * unresolved domain gets.
 */
const DOMAIN_COLORS: Record<string, string> = {
  "Orders & Fulfillment": "#c2410c",
  "Shopify & Storefront": "#7c3aed",
  Collections: "#0e7490",
  "Klydos & Content": "#0369a1",
  "Devices & Clocks": "#15803d",
  "Users & Accounts": "#a16207",
  "Payments & Payouts": "#b91c1c",
  "Marketing & CRM": "#be185d",
  Studio: "#4338ca",
  Backoffice: "#525252",
  "Mobile App": "#0d9488",
  Editor: "#9333ea",
  "Cloud Functions": "#0891b2",
  Platform: "#57534e",
  Unknown: "#737373",
};

export const domainColor = (domain: string): string =>
  DOMAIN_COLORS[domain] ?? DOMAIN_COLORS.Unknown;

/**
 * A domain read off a `src/modules/<Module>/` frame is as good as fact; one
 * inferred from a route or keyword is a good guess; one that is only the Sentry
 * project's own area is barely more than a label. The chip says which.
 */
export const DOMAIN_SOURCE_LABELS: Record<ErrorDomainSource, string> = {
  module: "Matched the module the failing frame lives in",
  route: "Inferred from the route the request was serving",
  keyword: "Inferred from a keyword in the route",
  project: "Only the Sentry project is known — domain is the project's area",
  unknown: "Could not be determined",
};

/** Solid for a certain domain, outlined when it was inferred. */
export const isConfidentDomain = (source: ErrorDomainSource): boolean =>
  source === "module" || source === "route";

const LEVEL_COLORS: Record<string, string> = {
  fatal: "#7f1d1d",
  error: "#b91c1c",
  warning: "#b45309",
  info: "#0369a1",
  debug: "#525252",
};

export const levelColor = (level: string): string =>
  LEVEL_COLORS[level] ?? LEVEL_COLORS.debug;

/** Marks an issue that was resolved and came back. */
export const REGRESSION_COLOR = "#b45309";

/** "3m", "4h", "6d" — the table has no room for full timestamps. */
export const relativeTime = (timestamp: string | null): string => {
  if (!timestamp) return "—";
  const elapsedSeconds = (Date.now() - new Date(timestamp).getTime()) / 1000;
  if (elapsedSeconds < 60) return "just now";
  if (elapsedSeconds < 3600) return `${Math.floor(elapsedSeconds / 60)}m ago`;
  if (elapsedSeconds < 86400)
    return `${Math.floor(elapsedSeconds / 3600)}h ago`;
  return `${Math.floor(elapsedSeconds / 86400)}d ago`;
};

/** Full local timestamp, for the tooltip behind a relative time. */
export const absoluteTime = (timestamp: string | null): string =>
  timestamp ? new Date(timestamp).toLocaleString() : "unknown";

export const formatCount = (count: number): string => {
  if (count < 1000) return String(count);
  if (count < 1_000_000)
    return `${(count / 1000).toFixed(count < 10_000 ? 1 : 0)}k`;
  return `${(count / 1_000_000).toFixed(1)}M`;
};
